import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle} from "react";
import {
  Modal,
  Form,
  Row,
  Col,
  Select,
  Button,
  Space,
  message,
  InputNumber,
  Input
} from "antd";
import { HomeOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import dayjs from "dayjs";
import commonFn from "@/utils/commonFn";

const FieldRuleModal = forwardRef(({ submitInitFn }, ref) => {
    const [form] = Form.useForm();
    const [type, setType] = useState('add');
    const [oldState, setOldState] = useState({});
    const [modalName, setModalName] = useState('Payment Request');
    const [modalFlag, setModalFlag] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [btnDisabled, setBtnDisabled] = useState(false);
    const [entityNameList, setEntityNameList] = useState([]);
    const [conditionFieldList, setConditionFieldList] = useState([]);
    const [validationTypeKey, setValidationTypeKey] = useState(0);
    
    useImperativeHandle(ref, () => ({
      openModal: ({type, modalName, oldState}) => {
        setType(type)
        setModalName(modalName)
        setOldState(oldState)
        setModalFlag(true)
      }
    }));

    const afterOpenChangeFn = (flag) => {
      if(flag){
        if(type !== 'add'){
          getDetailFn()
        } else {
        }
      }else{
        setConditionFieldList([])
        form.resetFields();
        setBtnLoading(false)
        setBtnDisabled(false)
      }
    }

    const getDetailFn = async() => {
        setBtnDisabled(true)
        const { entityName, conditionField, conditionValue } = oldState
        const response = await http.get(api.fieldRuleById, { params: {entityName, conditionField, conditionValue} });
        form.setFieldsValue({
          ...response.data,
        });
        getConditionFieldList(response.data?.entityName, true)
        setBtnDisabled(false)
    }

    const submitAddOrEditFn = async(type) => {
      const flag = type === 'add'
      setBtnLoading(true)
      const response = await http.post(flag ? api.fieldRuleAdd : api.fieldRuleUpdate, {
        ...form.getFieldValue(),
      });
      if (response.code === "1") {
        message.success(`${flag ? 'New' : 'Edit'} successfully`);
        setModalFlag(false)
        submitInitFn();
      } else {
        commonFn.modalFailedFn(response)
      }
      setBtnLoading(false)
    }

    const submitFn = async() => {
      form.validateFields().then(value => {
        if(type === 'add' || type === 'edit'){
          submitAddOrEditFn(type)
        }
      }).catch(err =>{
        console.log(err,'===err');
      })
    }

    const getEntityNameList = async () => {
      const response = await http.get(api.fieldRuleTable);
      setEntityNameList(response?.data || []);
    };  

    const getConditionFieldList = async (tableName, flag) => {
      const response = await http.get(api.fieldRuleField, {params: {tableName}});
      setConditionFieldList(response?.data || []);
      if(!flag){
        form.setFieldsValue({ 'conditionField': '', 'targetFields': [] });
      }
    };  

    useEffect(() => {
      getEntityNameList();
    }, []);

  return (
    <Modal
      title={
        <div style={{ fontSize: "24px", fontWeight: "bold", color: "#3c8dbc" }}>
          <HomeOutlined style={{ marginRight: "8px" }} />
          {modalName}
          <div
            style={{ borderTop: "3px solid #3c8dbc", marginTop: "8px" }}
          ></div>
        </div>
      }
      centered
      open={modalFlag}
      onCancel={() => setModalFlag(false)}
      footer={null}
      maskClosable={false}
      afterOpenChange={afterOpenChangeFn}
      width="30%"
    >
      <Form
        form={form}
        layout="horizontal"
        labelCol={{span: 8}}
        wrapperCol={{span: 16}}
        labelWrap={true}
        labelAlign="right"
        initialValues={{
          sortOrder: 0
        }}
        validateMessages={{
          required: "${label} is required!",
        }}
      >
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              name="entityName"
              label="Entity Name"
              rules={[{ required: true, message: 'Entity Name is required!' }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Select a type"
                onChange={(value, option) => {if(value) {getConditionFieldList(value)}}}
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={entityNameList}
              />
            </Form.Item>

            <Form.Item
              name="conditionField"
              label="Condition Field"
              rules={[{ required: true, message: 'Condition Field is required!' }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Select a type"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                fieldNames={{
                  value: 'fieldName',
                  label: 'label',
                }}
                options={conditionFieldList}
              />
            </Form.Item>

            <Form.Item
              name="conditionOperator"
              label="Condition Operator"
              rules={[{ required: true, message: 'Condition Operator is required!' }]}
            >
              <Select
                placeholder="Select a type"
                options={[
                    {label: '=', value: '='},
                    {label: '!=', value: '!='},
                    {label: 'IN', value: 'IN'},
                ]}
              />
            </Form.Item>

            <Form.Item
              name="conditionValue"
              label="Condition Value"
              rules={[{ required: true, message: 'Condition Value is required!' }]}
            >
              <Input
                placeholder="Please input Condition Value"
              />
            </Form.Item>

            <Form.Item
              name="targetFields"
              label="Target Fields"
              rules={[{ required: true, message: 'Target Fields is required!' }]}
            >
              <Select
                showSearch
                allowClear
                mode="multiple"
                placeholder="Select a type"
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                fieldNames={{
                  value: 'fieldName',
                  label: 'label',
                }}
                options={conditionFieldList}
              />
            </Form.Item>

            <Form.Item
              name="validationType"
              label="Validation Type"
              rules={[{ required: true, message: 'Validation Type is required!' }]}
            >
              <Select
                placeholder="Select a type"
                onChange={(e) => setValidationTypeKey(+new Date())}
                options={[
                    {label: 'Required', value: 'Required'},
                    {label: 'Regex', value: 'Regex'},
                    {label: 'Range', value: 'Range'},
                ]}
              />
            </Form.Item>

            { ['Regex', 'Range'].includes(form.getFieldValue().validationType) && (
              <Form.Item
                name="validationConfig"
                label="Validation Config"
                rules={[{ required: true, message: 'Validation Config is required!' }]}
              >
                <Input
                  placeholder="Please input Validation Config"
                />
              </Form.Item>
            ) || null}

            <Form.Item
              name="remark"
              label="Remark"
            >
                <Input.TextArea
                    placeholder="Please input Remark"
                    autoSize={{ minRows: 3, maxRows: 10 }}
                />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <div style={{textAlign:'center'}}>
        <Space align="center">
          {/* <Button key="back" onClick={testFn}>
            TEST
          </Button> */}
          <Button key="back" onClick={() => setModalFlag(false)}>
            Cancel
          </Button>
          {
            type !== 'view' && (
              <Button type="primary" onClick={() => submitFn()} loading={btnLoading} disabled={btnDisabled}>
                Submit
              </Button>
            ) || null
          }
        </Space>
      </div>
    </Modal>
  );
});

export default FieldRuleModal;